import FlexSearch from "flexsearch";
import { openFile } from "../utils/LocalFileController/LocalFileController";
import { isIgnoredPath } from "./CodeCanvasIgnore";
import { logger } from "./Logger";
import {
  createFileMetadata,
  estimateTokenCount,
  getByteSize,
} from "./TokenCounter";

interface RepoItem {
  path: string;
  type: string;
  fileName?: string;
}

// Create a Set from text-extensions array for faster lookups
const textExtensions = [
  "txt",
  "md",
  "markdown",
  "js",
  "jsx",
  "ts",
  "tsx",
  "json",
  "html",
  "htm",
  "css",
  "scss",
  "less",
  "py",
  "rb",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "cs",
  "go",
  "php",
  "pl",
  "sh",
  "bash",
  "xml",
  "yaml",
  "yml",
  "ini",
  "cfg",
  "conf",
  "toml",
  "sql",
  "graphql",
  "swift",
  "kt",
  "rs",
  "dart",
  "lua",
  "r",
  "vue",
  "svelte",
  "jsx",
  "tsx",
  "mjs",
  "cjs",
  "ejs",
  "pug",
  "jade",
  "haml",
  "hbs",
  "handlebars",
  "m",
  "mm",
  "f",
  "f90",
  "ex",
  "exs",
  "erl",
  "hrl",
  "clj",
  "cljs",
  "scala",
  "sc",
  "groovy",
  "gradle",
  "tf",
  "tfvars",
  "proto",
  "sol",
  "asm",
  "s",
  "d",
  "elm",
  "jl",
  "zig",
  "v",
  "nim",
  "cr",
  "ml",
  "mli",
  "fs",
  "fsi",
  "fsx",
  "fsscript",
  "tcl",
  "vb",
  "ps1",
  "psm1",
  "psd1",
];
const TEXT_EXTENSIONS = new Set(textExtensions);

const isTextFile = (filename: string) => {
  const ext = filename.split(".").pop()?.toLowerCase();
  return ext ? TEXT_EXTENSIONS.has(ext) : true;
};

const MAX_FILE_SIZE = 3000 * 1024; // 3000KB limit
const BATCH_SIZE = 20; // Process 20 files at a time

interface IndexingProgress {
  total: number;
  current: number;
  isIndexing: boolean;
}

interface IndexingResult {
  fileIndex: FlexSearch.Index;
  documentMap: Map<string, any>;
  indexingProgress: IndexingProgress;
}

export async function buildFlexSearchIndex(
  repo: Record<string, RepoItem>,
  currentRepo: string,
  onProgressUpdate: (progress: IndexingProgress) => void,
  preloadedContent?: Record<
    string,
    { path: string; content: string; fileName: string; type: string }
  >
): Promise<IndexingResult> {
  logger.debug.info("DEBUGGING_AI_QUALITY: FlexSearch Indexing - Starting", {
    totalRepoItems: Object.keys(repo).length,
    currentRepo: currentRepo,
    hasPreloadedContent: !!preloadedContent,
    preloadedContentCount: Object.keys(preloadedContent || {}).length,
    timestamp: new Date().toISOString(),
  });

  logger.debug.info("[FlexSearchIndexer] Starting indexing process");

  // Build FlexSearch index
  const fileIndex = new FlexSearch.Index({
    tokenize: "forward",
    cache: true,
    context: true,
  });

  // Create a new Map for documents
  const newDocumentMap = new Map<string, any>();

  // Filter files to process - only text files
  const filesToProcess = Object.values(repo).filter(
    (item: RepoItem) =>
      item.path !== currentRepo &&
      item.type !== "tree" &&
      isTextFile(item.path) &&
      !isIgnoredPath(item.path)
  );

  logger.debug.info(
    "DEBUGGING_AI_QUALITY: FlexSearch Indexing - File Filtering",
    {
      totalItems: Object.keys(repo).length,
      textFilesToProcess: filesToProcess.length,
      filteredOutCount: Object.keys(repo).length - filesToProcess.length,
      fileTypes: [
        ...new Set(
          filesToProcess.map((f) => f.path.split(".").pop()).filter(Boolean)
        ),
      ],
      avgPathLength:
        filesToProcess.reduce((sum, f) => sum + f.path.length, 0) /
        filesToProcess.length,
      timestamp: new Date().toISOString(),
    }
  );

  // Set initial progress
  const totalFiles = filesToProcess.length;
  logger.debug.info(
    `[FlexSearchIndexer] Total files to process: ${totalFiles}`
  );

  onProgressUpdate({
    total: totalFiles,
    current: 0,
    isIndexing: true,
  });

  let processedCount = 0;
  let batchNumber = 0;
  let lastProgressUpdate = 0;
  let totalIndexedDocuments = 0;
  let totalBytes = 0;
  let totalTokens = 0;
  let largeFilesCount = 0;

  // Create a Web Worker for processing
  const worker = new Worker(new URL("./FlexSearchWorker.ts", import.meta.url), {
    type: "module",
  });

  try {
    // Process files in batches
    for (let i = 0; i < filesToProcess.length; i += BATCH_SIZE) {
      batchNumber++;
      const currentBatch = filesToProcess.slice(i, i + BATCH_SIZE);

      logger.debug.info(
        "DEBUGGING_AI_QUALITY: FlexSearch Indexing - Batch Processing",
        {
          batchNumber: batchNumber,
          batchSize: currentBatch.length,
          startIndex: i,
          endIndex: Math.min(i + BATCH_SIZE, filesToProcess.length),
          progress: `${Math.round((i / filesToProcess.length) * 100)}%`,
          timestamp: new Date().toISOString(),
        }
      );

      const batchWithContents = await Promise.all(
        currentBatch.map(async (file: RepoItem) => {
          try {
            // Use preloaded content if available, otherwise load from disk
            let fileResult: any;
            if (preloadedContent && preloadedContent[file.path]) {
              fileResult = { fileContent: preloadedContent[file.path].content };
            } else {
              fileResult = await openFile(file.path, null, false);
            }

            const content = fileResult?.fileContent;

            return {
              ...file,
              content:
                content &&
                typeof content === "string" &&
                content.length <= MAX_FILE_SIZE
                  ? content.slice(0, MAX_FILE_SIZE)
                  : null,
            };
          } catch (error) {
            console.warn(
              `[FlexSearchIndexer] Error reading file ${file.path}:`,
              error
            );
            return { ...file, content: null };
          }
        })
      );

      // Track batch statistics
      const batchStats = {
        successfulFiles: batchWithContents.filter((f) => f.content).length,
        failedFiles: batchWithContents.filter((f) => !f.content).length,
        totalBytes: batchWithContents.reduce(
          (sum, f) => sum + (f.content?.length || 0),
          0
        ),
        largeFiles: batchWithContents.filter(
          (f) => f.content && f.content.length > MAX_FILE_SIZE
        ).length,
      };

      // Process batch in Web Worker
      const batchResults = await new Promise<{
        type: string;
        results: Array<{
          success: boolean;
          document: {
            path: string;
            content: string;
            fileName: string;
            type: string;
          };
          docId: number;
        }>;
        batchSuccessCount: number;
        batchIndex: number;
      }>((resolve, reject) => {
        // Set up message handler for this batch
        const messageHandler = (event: MessageEvent) => {
          const data = event.data;
          if (data.type === "BATCH_COMPLETED" && data.batchIndex === i) {
            worker.removeEventListener("message", messageHandler);
            resolve(data);
          } else if (data.type === "ERROR") {
            worker.removeEventListener("message", messageHandler);
            reject(new Error(data.error || "Unknown worker error"));
          }
        };

        worker.addEventListener("message", messageHandler);

        // Send batch to worker
        worker.postMessage({
          command: "PROCESS_BATCH",
          batchFiles: batchWithContents,
          batchIndex: i,
        });
      });

      const { results, batchSuccessCount } = batchResults;

      // Add indexed documents to the main index
      for (const result of results) {
        if (result.success) {
          try {
            // Create file metadata with size information
            const metadata = createFileMetadata(result.document.content);

            // Enhanced document with metadata
            const enhancedDocument = {
              ...result.document,
              metadata: metadata,
              // Also store individual fields for backward compatibility
              tokenCount: metadata.tokenCount,
              byteSize: metadata.byteSize,
              exceedsThresholds: metadata.exceedsThresholds,
            };

            fileIndex.add(
              result.docId.toString(),
              result.document.path + " " + result.document.content
            );
            newDocumentMap.set(result.docId.toString(), enhancedDocument);

            // Update totals
            totalIndexedDocuments++;
            totalBytes += metadata.byteSize;
            totalTokens += metadata.tokenCount;
            if (metadata.exceedsThresholds) {
              largeFilesCount++;
            }
          } catch (error) {
            console.warn(
              `[FlexSearchIndexer] Error indexing document ${result.document.path}:`,
              error
            );
          }
        }
      }

      processedCount += batchSuccessCount;

      logger.debug.info(
        "DEBUGGING_AI_QUALITY: FlexSearch Indexing - Batch Completed",
        {
          batchNumber: batchNumber,
          batchStats: batchStats,
          workerResults: {
            successCount: batchSuccessCount,
            totalResults: results.length,
          },
          cumulativeStats: {
            processedCount: processedCount,
            totalIndexedDocuments: totalIndexedDocuments,
            totalBytes: totalBytes,
            totalTokens: totalTokens,
            largeFilesCount: largeFilesCount,
          },
          timestamp: new Date().toISOString(),
        }
      );

      logger.debug.info(
        `[FlexSearchIndexer] Batch ${batchNumber} complete: ${batchSuccessCount}/${currentBatch.length} files processed successfully`
      );

      // Throttle progress updates to prevent React infinite loops
      // Only update progress every 5 batches or if significant progress change (>5%)
      const currentProgress = Math.round((processedCount / totalFiles) * 100);
      const shouldUpdateProgress =
        batchNumber % 5 === 0 || // Every 5 batches
        currentProgress - lastProgressUpdate >= 5 || // Progress change >= 5%
        processedCount === totalFiles; // Final update

      if (shouldUpdateProgress) {
        lastProgressUpdate = currentProgress;
        const progress = {
          total: totalFiles,
          current: processedCount,
          isIndexing: true,
        };
        logger.debug.info(
          `[FlexSearchIndexer] Progress update: ${processedCount}/${totalFiles} (${currentProgress}%)`
        );
        onProgressUpdate(progress);
      }
    }

    // Terminate the worker when done
    worker.terminate();

    logger.debug.info("DEBUGGING_AI_QUALITY: FlexSearch Indexing - Completed", {
      totalFilesProcessed: processedCount,
      totalFilesToProcess: totalFiles,
      successRate: `${Math.round((processedCount / totalFiles) * 100)}%`,
      documentMapSize: newDocumentMap.size,
      totalIndexedDocuments: totalIndexedDocuments,
      totalBytes: totalBytes,
      totalTokens: totalTokens,
      avgTokensPerFile:
        totalIndexedDocuments > 0
          ? Math.round(totalTokens / totalIndexedDocuments)
          : 0,
      largeFilesCount: largeFilesCount,
      largeFilesPercentage: `${Math.round(
        (largeFilesCount / totalIndexedDocuments) * 100
      )}%`,
      avgBytesPerFile:
        totalIndexedDocuments > 0
          ? Math.round(totalBytes / totalIndexedDocuments)
          : 0,
      timestamp: new Date().toISOString(),
    });

    logger.debug.info(
      `[FlexSearchIndexer] Indexing complete. Successfully processed ${processedCount}/${totalFiles} files`
    );
    logger.debug.info(
      `[FlexSearchIndexer] Document map size: ${newDocumentMap.size}`
    );

    // Return final result
    return {
      fileIndex,
      documentMap: newDocumentMap,
      indexingProgress: {
        total: totalFiles,
        current: processedCount,
        isIndexing: false,
      },
    };
  } catch (error) {
    logger.debug.info("DEBUGGING_AI_QUALITY: FlexSearch Indexing - Error", {
      error: error.message,
      stack: error.stack,
      processedCount: processedCount,
      totalFiles: totalFiles,
      timestamp: new Date().toISOString(),
    });

    console.error("[FlexSearchIndexer] Fatal error during indexing:", error);
    throw error;
  }
}
